/**
 * AppointmentWhere: the one "Where" block an appointment's place is set
 * in, on the record form and on the confirm card alike (the two surfaces
 * share this component so they match).
 *
 * Where reads as a place, then its address (LP-26). While the host holds
 * a place (a Directory pick, from the name box or from the provider
 * picker, or a care-team provider's office), the block is one line: the
 * place name, the address under it on one line, where it came from ("from
 * the Directory", or "from the care team" for a row with no Directory
 * link), and the quiet actions Change (the name box comes back to pick
 * another entry), Use another place (the held place is dropped; the name
 * and the boxes keep what they hold, editable) and Edit address (the
 * five boxes open, filled). The boxes also show when the held place has
 * no address to derive (a provider in the Directory with none: its name
 * and link fill Where, and the address is typed), or an address box
 * carries an error. With no
 * place held the block is the name box and the boxes, as typed.
 *
 * A provider pick never fills a Where that already holds a name or an
 * address: the host passes its place as `offer`, and one quiet action
 * under the block reads "Use <place> instead". Nothing fills on its own,
 * and nothing typed is ever discarded by a pick, a change or a clear.
 *
 * The stored fields do not change: location_name, location_address and
 * the two Directory ids travel as before. Only the presentation is here.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { AddressFields, type AddressFieldsProps } from '@/components/clientRecord/AddressesCard';
import { DirectoryNameBox } from '@/components/clientRecord/DirectoryNameBox';
import { Button } from '@/components/primitives/Button';
import { Field, fieldBoxAria } from '@/components/primitives/Field';
import { formatAddress } from '@/lib/address';
import { recordFieldLabel } from '@/lib/recordFormWords';

export type WhereDirectoryEntry = { id: string; name: string; type: 'organization' | 'individual' };

/** The block's words, shared with the tests. */
export const WHERE_HEADING = 'Where';
/** The name line's label is the record form's own word for the field. */
export const WHERE_NAME_LABEL = recordFieldLabel('appointments', 'location_name');
export const WHERE_ADDRESS_LABEL = recordFieldLabel('appointments', 'location_address');
export const WHERE_FROM_DIRECTORY = 'from the Directory';
export const WHERE_FROM_CARE_TEAM = 'from the care team';
export const WHERE_CHANGE = 'Change';
export const WHERE_USE_ANOTHER = 'Use another place';
export const WHERE_EDIT_ADDRESS = 'Edit address';
/** The one action a provider pick offers over a Where already set. */
export function whereUseInstead(place: string): string {
  return `Use ${place} instead`;
}

export interface AppointmentWhereProps {
  /** The name box's id (its Field's htmlFor). */
  nameId: string;
  /** The name box's data-testid (each surface keeps its own). */
  nameTestId: string;
  /** The address boxes' id prefix (each surface keeps its own). */
  addressIdPrefix: string;
  /** The place name as the box holds it. */
  name: string;
  /** A keystroke in the name box: the host keeps the text and drops the
   *  pick. */
  onNameChange: (next: string) => void;
  /** A Directory pick: the host records it and copies the entry's name
   *  and address into the boxes. */
  onPick: (entry: WhereDirectoryEntry) => void;
  /** The place the host holds, if any: a Directory entry, or a care-team
   *  provider's office ('care_team', a row with no Directory link). */
  link: { name: string; from?: 'directory' | 'care_team' } | null;
  /** True after Change: the box shows over the held pick until a pick
   *  or a keystroke. */
  searching: boolean;
  /** Change: open the box again. */
  onChange: () => void;
  /** Use another place: drop the pick, keep what the boxes hold. */
  onClear: () => void;
  /** A picked entry whose address is text only. */
  directoryText: string | null;
  /** A provider pick's place, offered over a Where already set. */
  offer?: { name: string; onUse: () => void } | null;
  /** The address boxes. */
  address: Omit<AddressFieldsProps, 'idPrefix'>;
  /** The data-testid on the address boxes' group (each surface keeps its
   *  own field name for it). */
  addressTestId: string;
  /** The confirm card's "From the note" line, or the form's "On file
   *  as" line: anything the surface shows above the name line. */
  above?: ReactNode;
  /** Anything the surface shows under the boxes (the address check's
   *  prompt on the form). */
  children?: ReactNode;
  /** 'form' draws the record form's label style; 'card' the confirm
   *  card's smaller one. */
  surface: 'form' | 'card';
}

/** The address the boxes hold as one line, or null when they hold none
 *  (State alone, which a new form starts filled, is no address). */
export function whereAddressLine(values: AddressFieldsProps['values']): string | null {
  const any = [values.line1, values.line2, values.city, values.zip].some((v) => (v ?? '').trim() !== '');
  if (!any) return null;
  return formatAddress({
    line1: values.line1 ?? '',
    line2: values.line2 ?? undefined,
    city: values.city ?? undefined,
    state: values.state ?? undefined,
    zip: values.zip ?? undefined,
  });
}

export function AppointmentWhere({
  nameId,
  nameTestId,
  addressIdPrefix,
  name,
  onNameChange,
  onPick,
  link,
  searching,
  onChange,
  onClear,
  directoryText,
  offer = null,
  address,
  addressTestId,
  above,
  children,
  surface,
}: AppointmentWhereProps): ReactNode {
  // The block's heading sits a step above the two labels inside it; the
  // form's label row and the card's share it.
  const legendClass = `text-sm font-semibold text-sage-800 ${surface === 'form' ? 'mb-[7px]' : 'mb-1'}`;
  const picked = link !== null && !searching;
  // Edit address opens the boxes over a held place; a new place closes
  // them again.
  const [editing, setEditing] = useState(false);
  const heldName = link?.name ?? null;
  useEffect(() => setEditing(false), [heldName]);
  const derived = whereAddressLine(address.values) ?? directoryText;
  const boxError = [address.line1Error, address.cityError, address.stateError, address.zipError].some(
    (e) => e !== undefined && e !== null && e !== '',
  );
  const boxes = link === null || editing || derived === null || boxError;
  // A keystroke in boxes a held place shows open (it had no address to
  // derive) keeps them open, so the first letter typed does not fold the
  // boxes away under the person typing.
  const keepOpen =
    (change: (v: string) => void) =>
    (v: string): void => {
      if (link !== null) setEditing(true);
      change(v);
    };
  const boxProps: Omit<AddressFieldsProps, 'idPrefix'> = {
    ...address,
    onLine1Change: keepOpen(address.onLine1Change),
    onLine2Change: keepOpen(address.onLine2Change),
    onCityChange: keepOpen(address.onCityChange),
    onStateChange: keepOpen(address.onStateChange),
    onZipChange: keepOpen(address.onZipChange),
  };
  const editAddress =
    link !== null && !boxes ? (
      <Button variant="ghost" size="sm" data-testid="appointment-where-edit-address" onClick={() => setEditing(true)}>
        {WHERE_EDIT_ADDRESS}
      </Button>
    ) : null;
  const addressLine =
    link !== null && !boxes ? (
      <p data-testid="appointment-where-address" className="min-w-0 text-sm text-ink [overflow-wrap:anywhere]">
        {derived}
      </p>
    ) : null;
  return (
    <fieldset data-testid="appointment-where" className="flex min-w-0 flex-col gap-2">
      <legend className={legendClass}>{WHERE_HEADING}</legend>
      {above}
      {picked ? (
        <div data-testid="appointment-where-picked" className="flex min-w-0 flex-col gap-0.5 text-sm text-ink">
          <span className="min-w-0 font-semibold [overflow-wrap:anywhere]">{link.name}</span>
          {addressLine}
          <span className="text-xs text-ink-soft">
            {link.from === 'care_team' ? WHERE_FROM_CARE_TEAM : WHERE_FROM_DIRECTORY}
          </span>
          {/* The quiet actions' text lines up with the place above: the
              row steps left by a small ghost Button's own padding, so a
              wrapped row on a phone starts at the same edge. */}
          <div className="-ml-[14px] flex flex-wrap items-center gap-y-1">
            <Button variant="ghost" size="sm" data-testid="appointment-where-change" onClick={onChange}>
              {WHERE_CHANGE}
            </Button>
            <Button variant="ghost" size="sm" data-testid="appointment-where-clear" onClick={onClear}>
              {WHERE_USE_ANOTHER}
            </Button>
            {editAddress}
          </div>
        </div>
      ) : (
        <>
          <Field label={WHERE_NAME_LABEL} htmlFor={nameId}>
            <DirectoryNameBox
              id={nameId}
              testId={nameTestId}
              value={name}
              onChange={onNameChange}
              onPick={onPick}
              listLabel="Directory places"
              linked={false}
              boxAria={fieldBoxAria(nameId, {})}
            />
          </Field>
          {addressLine !== null && (
            <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
              {addressLine}
              {editAddress}
            </div>
          )}
        </>
      )}
      {boxes && directoryText !== null && (
        <p data-testid="appointment-location-directory-text" className="text-xs text-sage-800">
          From the Directory: {directoryText}
        </p>
      )}
      {boxes && (
        <fieldset data-testid={addressTestId} className="flex min-w-0 flex-col gap-2">
          {/* The group's name for a screen reader; on screen the boxes' own
              labels (Address line 1, City) say it. */}
          <legend className="sr-only">{WHERE_ADDRESS_LABEL}</legend>
          <AddressFields idPrefix={addressIdPrefix} {...boxProps} />
        </fieldset>
      )}
      {offer !== null && (
        <div className="-ml-[14px]">
          <Button variant="ghost" size="sm" data-testid="appointment-where-use-provider" onClick={offer.onUse}>
            {whereUseInstead(offer.name)}
          </Button>
        </div>
      )}
      {children}
    </fieldset>
  );
}
